import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, Cell, Legend,
} from 'recharts';
import {
  WGS_JOBS, PIPELINE_STAGES, WHO_GRADES, REFERENCE, LINEAGES,
  getPipelineStats, submitJob,
} from './data/pipelineData';

// ── DESIGN TOKENS — Terminal / Bioinformatics Aesthetic ───────────────
const T = {
  bg:       '#0A0E14', bgCard: '#0D1117', bgElevated: '#161B22',
  bgBorder: '#21262D', bgInput: '#161B22',
  green:    '#39D353', greenDim: '#196127', greenGlow: '#39D35340',
  cyan:     '#58C4DD', cyanDim: '#1B4F5F',
  amber:    '#D29922', amberDim: '#7D5A00',
  red:      '#F85149', redDim:  '#5E1212',
  purple:   '#BC8CFF', purpleDim: '#3D1A78',
  blue:     '#388BFD', blueDim:  '#0D419D',
  text:     '#E6EDF3', textMuted: '#8B949E', textDim: '#484F58',
  white:    '#FFFFFF',
};

const MONO = "'JetBrains Mono', 'Fira Code', 'Courier New', monospace";
const SANS = "'Inter', 'Segoe UI', system-ui, sans-serif";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Inter:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; min-height: 100vh; }
  body { background: ${T.bg}; color: ${T.text}; font-family: ${SANS}; font-size: 14px; line-height: 1.5; }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: ${T.bgCard}; }
  ::-webkit-scrollbar-thumb { background: ${T.bgBorder}; border-radius: 3px; }
  input, select, textarea, button { font-family: ${SANS}; }
  ::selection { background: ${T.cyan}40; color: ${T.cyan}; }
  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes spin { to{transform:rotate(360deg)} }
  @keyframes progress { from{width:0} to{width:var(--w)} }
  @keyframes scanline { 0%{top:-10%} 100%{top:110%} }
  @keyframes pulse { 0%,100%{opacity:1;box-shadow:0 0 6px currentColor} 50%{opacity:0.7;box-shadow:0 0 16px currentColor} }
  .fade-up { animation: fadeUp 0.25s ease forwards; }
  .terminal { font-family: ${MONO}; }
`;

// ── STATUS CONFIG ─────────────────────────────────────────────────────
const JOB_STATUS = {
  QUEUED:   { color: T.textMuted, bg: T.bgElevated, dot: T.textMuted, label: 'Queued'   },
  RUNNING:  { color: T.cyan,      bg: T.cyanDim,    dot: T.cyan,      label: 'Running'  },
  COMPLETE: { color: T.green,     bg: T.greenDim,   dot: T.green,     label: 'Complete' },
  FAILED:   { color: T.red,       bg: T.redDim,     dot: T.red,       label: 'Failed'   },
};

const DRUG_COLORS = {
  Susceptible: T.green, Resistant: T.red, Intermediate: T.amber, 'Not tested': T.textDim,
};

const TB_CLASS_COLORS = {
  'DS-TB': T.green, 'HR-TB': T.amber, 'RR-TB': T.red, 'MDR-TB': T.red,
  'Pre-XDR-TB': T.purple, 'XDR-TB': '#FF0040',
};

// ── HELPERS ───────────────────────────────────────────────────────────
const mono = (text, color = T.cyan, size = 12) => (
  <span style={{ fontFamily: MONO, fontSize: size, color }}>{text}</span>
);

const badge = (label, color, bg) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, color, background: bg || color + '20', border: `1px solid ${color}40` }}>
    {label}
  </span>
);

const card = (children, style = {}) => (
  <div style={{ background: T.bgCard, border: `1px solid ${T.bgBorder}`, borderRadius: 8, ...style }}>
    {children}
  </div>
);

const Spinner = ({ size = 16, color = T.cyan }) => (
  <div style={{ width: size, height: size, borderRadius: '50%', border: `2px solid ${color}30`, borderTopColor: color, animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
);

// ── TERMINAL LOG ──────────────────────────────────────────────────────
function TerminalLog({ job }) {
  const ref = useRef();
  const logs = job.status === 'COMPLETE' ? [
    { t: '10:07:01', level: 'INFO',  msg: `Pipeline started for ${job.sample_code}` },
    { t: '10:07:03', level: 'INFO',  msg: `Reference: ${REFERENCE.name} (${REFERENCE.accession})` },
    { t: '10:07:05', level: 'INFO',  msg: `Input reads: ${job.qc?.raw_reads?.toLocaleString()} (R1) + ${job.qc?.raw_reads_r2?.toLocaleString()} (R2)` },
    { t: '10:09:15', level: 'OK',    msg: `FastQC: Mean quality ${job.qc?.raw_quality_avg} — PASS` },
    { t: '10:09:18', level: 'WARN',  msg: `Adapter content: ${job.qc?.adapter_pct}% — trimming required` },
    { t: '10:15:22', level: 'OK',    msg: `Trimmomatic: ${job.qc?.trimmed_reads?.toLocaleString()} reads retained (${job.qc?.trimmed_pct}%)` },
    { t: '10:15:24', level: 'OK',    msg: `Post-trim FastQC: Mean quality ${job.qc?.trimmed_quality} — PASS` },
    { t: '10:27:31', level: 'OK',    msg: `BWA-MEM: Mapping rate ${job.qc?.mapping_rate}% — PASS` },
    { t: '10:35:48', level: 'OK',    msg: `Mean depth: ${job.qc?.mean_depth}x | Coverage: ${job.qc?.genome_coverage}%` },
    { t: '10:35:50', level: 'INFO',  msg: `Duplicate rate: ${job.qc?.duplicate_pct}% — within acceptable range` },
    { t: '10:50:12', level: 'OK',    msg: `GATK HaplotypeCaller: ${job.variants?.length || 0} variants called` },
    { t: '10:55:03', level: 'OK',    msg: `WHO 2021 Catalogue: Annotated ${job.variants?.filter(v => v.who_grade <= 2).length || 0} resistance-associated variants` },
    { t: '10:59:18', level: 'ALERT', msg: `rpoB S450L detected — Grade 1 (Rifampicin resistance confirmed)` },
    { t: '10:59:19', level: 'ALERT', msg: `katG S315T detected — Grade 1 (Isoniazid resistance confirmed)` },
    { t: '10:59:20', level: 'ALERT', msg: `gyrA D94G detected — Grade 1 (Fluoroquinolone resistance)` },
    { t: '11:03:44', level: 'OK',    msg: `TB-Profiler lineage: ${job.lineage?.main_lineage} (${job.lineage?.sub_lineage})` },
    { t: '11:06:51', level: 'OK',    msg: `ML k-mer model: accuracy ${(job.ml_prediction?.accuracy * 100).toFixed(1)}% | AUC-ROC ${job.ml_prediction?.auc_roc}` },
    { t: '11:06:52', level: 'ALERT', msg: `CLASSIFICATION: ${job.classification} — WGS confirms Pre-XDR-TB` },
    { t: '11:06:55', level: 'OK',    msg: `FHIR R4 report generated | DHIS2 data value set ready` },
    { t: '11:06:58', level: 'OK',    msg: `Pipeline complete. Total runtime: 59m 57s` },
  ] : job.status === 'RUNNING' ? [
    { t: '09:02:01', level: 'INFO',  msg: `Pipeline started for ${job.sample_code}` },
    { t: '09:02:03', level: 'INFO',  msg: `Reference: ${REFERENCE.name} (${REFERENCE.accession})` },
    { t: '09:02:05', level: 'INFO',  msg: `Input reads: ${job.qc?.raw_reads?.toLocaleString()} (R1+R2)` },
    { t: '09:06:14', level: 'OK',    msg: `FastQC: Mean quality ${job.qc?.raw_quality_avg} — PASS` },
    { t: '09:12:30', level: 'OK',    msg: `Trimmomatic: ${job.qc?.trimmed_reads?.toLocaleString()} reads retained (${job.qc?.trimmed_pct}%)` },
    { t: '09:24:45', level: 'OK',    msg: `BWA-MEM: Mapping rate ${job.qc?.mapping_rate}% — PASS` },
    { t: '09:32:01', level: 'OK',    msg: `Mean depth: ${job.qc?.mean_depth}x | Coverage: ${job.qc?.genome_coverage}%` },
    { t: '09:40:12', level: 'INFO',  msg: `SAMtools: Sorting and indexing BAM...` },
    { t: '09:44:55', level: 'OK',    msg: `Picard MarkDuplicates: ${job.qc?.duplicate_pct}% duplicates marked` },
    { t: '09:45:00', level: 'INFO',  msg: `GATK HaplotypeCaller: Running variant calling... [62%]` },
    { t: '09:45:01', level: 'RUN',   msg: `▶ Active: Variant calling in progress...` },
  ] : [
    { t: new Date().toTimeString().slice(0,8), level: 'INFO', msg: `Job ${job.id} queued — awaiting compute slot` },
    { t: new Date().toTimeString().slice(0,8), level: 'INFO', msg: `Priority: ${job.priority} | Trigger: ${job.trigger_reason}` },
  ];

  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [job]);

  const levelColor = { INFO: T.textMuted, OK: T.green, WARN: T.amber, ALERT: T.red, RUN: T.cyan, ERROR: T.red };

  return (
    <div ref={ref} style={{ background: '#010409', border: `1px solid ${T.bgBorder}`, borderRadius: 8, padding: 16, maxHeight: 280, overflowY: 'auto', fontFamily: MONO }}>
      <div style={{ fontSize: 11, color: T.textDim, marginBottom: 12 }}>
        ● {job.sample_code} · Pipeline log · {job.status}
        {job.status === 'RUNNING' && <span style={{ color: T.cyan, marginLeft: 8, animation: 'blink 1s infinite' }}>■ LIVE</span>}
      </div>
      {logs.map((log, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 3, fontSize: 11, lineHeight: 1.6 }}>
          <span style={{ color: T.textDim, flexShrink: 0 }}>[{log.t}]</span>
          <span style={{ color: levelColor[log.level] || T.textMuted, flexShrink: 0, fontWeight: 600, width: 44 }}>{log.level}</span>
          <span style={{ color: log.level === 'ALERT' ? T.red : log.level === 'OK' ? T.green : log.level === 'RUN' ? T.cyan : T.textMuted }}>{log.msg}</span>
        </div>
      ))}
      {job.status === 'RUNNING' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
          <Spinner size={10} />
          <span style={{ fontFamily: MONO, fontSize: 11, color: T.cyan, animation: 'blink 1s infinite' }}>_</span>
        </div>
      )}
    </div>
  );
}

// ── PIPELINE FLOW ─────────────────────────────────────────────────────
function PipelineFlow({ job }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, overflowX: 'auto', paddingBottom: 8 }}>
      {job.stages.map((stage, i) => {
        const isDone    = stage.status === 'complete';
        const isRunning = stage.status === 'running';
        const isPending = stage.status === 'pending';
        const color     = isDone ? T.green : isRunning ? T.cyan : T.textDim;
        return (
          <React.Fragment key={stage.id}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72, position: 'relative' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isDone ? T.green + '20' : isRunning ? T.cyan + '20' : T.bgElevated,
                border: `2px solid ${color}`, fontSize: 16, position: 'relative', zIndex: 1,
                boxShadow: isRunning ? `0 0 12px ${T.cyan}60` : 'none',
                animation: isRunning ? 'pulse 2s infinite' : 'none',
              }}>
                {isRunning ? <Spinner size={16} /> : <span>{isDone ? '✓' : stage.icon}</span>}
              </div>
              {stage.progress !== null && stage.progress !== undefined && (
                <div style={{ position: 'absolute', bottom: -4, left: '50%', transform: 'translateX(-50%)', fontSize: 9, color: T.cyan, fontFamily: MONO, whiteSpace: 'nowrap' }}>
                  {stage.progress}%
                </div>
              )}
              <div style={{ fontSize: 9, color, textAlign: 'center', marginTop: 6, lineHeight: 1.3, maxWidth: 64 }}>{stage.label}</div>
              <div style={{ fontSize: 8, color: T.textDim, textAlign: 'center', marginTop: 2, fontFamily: MONO, maxWidth: 64 }}>{stage.duration}</div>
            </div>
            {i < job.stages.length - 1 && (
              <div style={{ flex: 1, height: 2, minWidth: 12, background: isDone ? T.green + '60' : T.bgBorder, marginBottom: 24 }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── QC PANEL ──────────────────────────────────────────────────────────
function QCPanel({ qc }) {
  if (!qc) return <div style={{ padding: 24, textAlign: 'center', color: T.textDim }}>QC data not yet available</div>;

  const metrics = [
    { label: 'Raw Reads (R1+R2)',  value: `${(qc.raw_reads * 2 / 1e6).toFixed(1)}M`,  status: qc.raw_reads > 1e6 ? 'PASS' : 'FAIL'  },
    { label: 'Mean Raw Quality',   value: `Q${qc.raw_quality_avg}`,                    status: qc.raw_quality_avg >= 30 ? 'PASS' : 'WARN' },
    { label: 'GC Content',         value: `${qc.raw_gc_pct}%`,                         status: qc.raw_gc_pct > 60 && qc.raw_gc_pct < 70 ? 'PASS' : 'WARN' },
    { label: 'Adapter Content',    value: `${qc.adapter_pct}%`,                        status: qc.adapter_pct < 10 ? 'PASS' : 'WARN' },
    { label: 'Reads After Trim',   value: `${qc.trimmed_pct}%`,                        status: qc.trimmed_pct > 90 ? 'PASS' : 'WARN' },
    { label: 'Post-trim Quality',  value: `Q${qc.trimmed_quality}`,                    status: qc.trimmed_quality >= 35 ? 'PASS' : 'WARN' },
    { label: 'Mapping Rate',       value: `${qc.mapping_rate}%`,                       status: qc.mapping_rate >= 95 ? 'PASS' : 'FAIL' },
    { label: 'Mean Depth',         value: `${qc.mean_depth}×`,                         status: qc.mean_depth >= 30 ? 'PASS' : 'FAIL'  },
    { label: 'Genome Coverage',    value: `${qc.genome_coverage}%`,                    status: qc.genome_coverage >= 95 ? 'PASS' : 'FAIL' },
    { label: 'Duplicate Rate',     value: `${qc.duplicate_pct}%`,                      status: qc.duplicate_pct < 5 ? 'PASS' : 'WARN' },
  ];

  const statusColors = { PASS: T.green, WARN: T.amber, FAIL: T.red };

  const depthData = [20,40,60,80,100,120,140,160,180].map(d => ({ depth: `${d}×`, pct: Math.max(0, 100 - Math.abs(d - qc.mean_depth) * 1.5 + Math.random() * 15) }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 16 }}>
        {metrics.map(m => (
          <div key={m.label} style={{ padding: '10px 12px', background: T.bgElevated, borderRadius: 6, borderLeft: `3px solid ${statusColors[m.status]}` }}>
            <div style={{ fontSize: 10, color: T.textDim, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{m.label}</div>
            <div style={{ fontFamily: MONO, fontSize: 15, fontWeight: 700, color: statusColors[m.status] }}>{m.value}</div>
            <div style={{ fontSize: 9, color: statusColors[m.status], marginTop: 2 }}>{m.status}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Coverage Depth Distribution</div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={depthData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <XAxis dataKey="depth" tick={{ fontSize: 9, fill: T.textDim }} />
              <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
              <Tooltip contentStyle={{ background: T.bgCard, border: `1px solid ${T.bgBorder}`, borderRadius: 6, fontSize: 11 }} />
              <Bar dataKey="pct" fill={T.cyan} radius={[2,2,0,0]} opacity={0.8} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Reference Alignment Summary</div>
          <div style={{ fontFamily: MONO, fontSize: 11, color: T.textMuted, lineHeight: 2 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${T.bgBorder}`, paddingBottom: 4, marginBottom: 4 }}>
              <span>Reference genome</span><span style={{ color: T.cyan }}>{qc.reference_genome}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Genome size</span><span style={{ color: T.text }}>{(qc.genome_size_bp / 1e6).toFixed(2)} Mbp</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Mapped reads</span><span style={{ color: T.green }}>{qc.mapping_rate}%</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Mean depth</span><span style={{ color: T.green }}>{qc.mean_depth}×</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Coverage ≥1×</span><span style={{ color: T.green }}>{qc.genome_coverage}%</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Duplicates</span><span style={{ color: qc.duplicate_pct < 5 ? T.green : T.amber }}>{qc.duplicate_pct}%</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── VARIANT TABLE ─────────────────────────────────────────────────────
function VariantTable({ variants }) {
  if (!variants || variants.length === 0) return <div style={{ padding: 24, textAlign: 'center', color: T.textDim }}>No variants available yet</div>;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: MONO, fontSize: 12 }}>
        <thead>
          <tr style={{ background: T.bgElevated }}>
            {['Gene', 'Position', 'Ref', 'Alt', 'AA Change', 'Type', 'Drug', 'Reads REF', 'Reads ALT', 'WHO Grade', 'Confidence'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, color: T.textDim, fontFamily: SANS, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: `1px solid ${T.bgBorder}`, whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {variants.map((v, i) => {
            const grade = WHO_GRADES[v.who_grade];
            const af = (v.reads_alt / (v.reads_ref + v.reads_alt) * 100).toFixed(1);
            return (
              <tr key={i} style={{ borderBottom: `1px solid ${T.bgBorder}`, transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = T.bgElevated}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '9px 12px', color: T.amber, fontWeight: 700 }}>{v.gene}</td>
                <td style={{ padding: '9px 12px', color: T.textMuted }}>{v.pos.toLocaleString()}</td>
                <td style={{ padding: '9px 12px', color: T.green }}>{v.ref}</td>
                <td style={{ padding: '9px 12px', color: T.red }}>{v.alt}</td>
                <td style={{ padding: '9px 12px', color: T.cyan, fontWeight: 700 }}>{v.aa}</td>
                <td style={{ padding: '9px 12px', color: T.textMuted }}>{v.type}</td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, background: T.red + '20', color: T.red, border: `1px solid ${T.red}30` }}>{v.drug}</span>
                </td>
                <td style={{ padding: '9px 12px', color: T.textMuted, textAlign: 'right' }}>{v.reads_ref}</td>
                <td style={{ padding: '9px 12px', color: T.red, textAlign: 'right', fontWeight: 700 }}>{v.reads_alt} ({af}%)</td>
                <td style={{ padding: '9px 12px' }}>
                  {grade && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, color: grade.color, background: grade.bg, border: `1px solid ${grade.color}40` }}>Grade {v.who_grade}</span>}
                </td>
                <td style={{ padding: '9px 12px' }}>
                  <span style={{ fontSize: 11, color: v.confidence === 'High' ? T.green : T.amber }}>{v.confidence}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: 12, padding: '10px 14px', background: T.bgElevated, borderRadius: 6, fontSize: 11, color: T.textMuted, fontFamily: SANS }}>
        <strong style={{ color: T.text }}>WHO 2021 Mutation Catalogue</strong> · Grade 1: Associated with resistance · Grade 2: Probable resistance · Grade 3: Uncertain significance · Allele frequency (AF) = ALT reads / total reads
      </div>
    </div>
  );
}

// ── ML PREDICTION PANEL ───────────────────────────────────────────────
function MLPanel({ ml }) {
  if (!ml) return <div style={{ padding: 24, textAlign: 'center', color: T.textDim }}>ML prediction not yet available</div>;

  const radarData = ml.predictions.map(p => ({ drug: p.drug.slice(0,4), prob: Math.round(p.prob * 100), resistant: p.prediction === 'Resistant' }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <div>
        <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>Model Details</div>
        <div style={{ fontFamily: MONO, fontSize: 11, color: T.textMuted, lineHeight: 2.2 }}>
          {[
            ['Model',        ml.model],
            ['k-mer size',   `k = ${ml.kmer_size}`],
            ['Feature count',`${ml.features.toLocaleString()} features`],
            ['Accuracy',     `${(ml.accuracy * 100).toFixed(1)}%`],
            ['AUC-ROC',      ml.auc_roc.toString()],
            ['Ref. genome',  'H37Rv (AL123456.3)'],
          ].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: `1px solid ${T.bgBorder}`, paddingBottom: 2 }}>
              <span style={{ color: T.textDim }}>{l}</span>
              <span style={{ color: T.cyan }}>{v}</span>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 14, padding: '10px 12px', background: T.purple + '15', border: `1px solid ${T.purple}40`, borderRadius: 6, fontSize: 11, color: T.textMuted, fontFamily: SANS }}>
          🤖 This model uses k-mer feature extraction on M. tb amino acid sequences — the same methodology as the TBGenSurv HIV drug resistance project, extended to M. tuberculosis and first/second-line TB drugs.
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>Drug Resistance Predictions</div>
        {ml.predictions.map((p, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{p.drug}</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: T.textDim }}>{(p.prob * 100).toFixed(1)}%</span>
                <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 99, color: DRUG_COLORS[p.prediction], background: DRUG_COLORS[p.prediction] + '20', border: `1px solid ${DRUG_COLORS[p.prediction]}40`, fontWeight: 600 }}>{p.prediction}</span>
                {p.actual && p.actual !== p.prediction && <span style={{ fontSize: 10, color: T.amber }}>⚠ DST: {p.actual}</span>}
              </div>
            </div>
            <div style={{ height: 6, background: T.bgElevated, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${p.prob * 100}%`, background: p.prediction === 'Resistant' ? T.red : T.green, borderRadius: 3, transition: 'width 0.8s ease' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── JOB DETAIL ────────────────────────────────────────────────────────
function JobDetail({ job, onClose }) {
  const [tab, setTab] = useState('pipeline');
  const tabs = [
    { id: 'pipeline',  label: '⚡ Pipeline Flow'       },
    { id: 'qc',        label: '🔍 QC Metrics'           },
    { id: 'variants',  label: `🧬 Variants (${job.variants?.length || 0})` },
    { id: 'lineage',   label: '🌍 Lineage'               },
    { id: 'ml',        label: '🤖 ML Prediction'         },
    { id: 'log',       label: '📟 Pipeline Log'          },
  ];

  const st = JOB_STATUS[job.status];
  const clsColor = TB_CLASS_COLORS[job.classification] || T.textMuted;

  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.bgBorder}`, borderRadius: 10, overflow: 'hidden' }} className="fade-up">
      {/* Job header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.bgBorder}`, background: T.bgElevated, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            {mono(job.sample_code, T.cyan, 16)}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, color: st.color, background: st.bg + '40', border: `1px solid ${st.color}40` }}>
              {job.status === 'RUNNING' && <Spinner size={10} color={st.color} />}
              {st.label}
            </span>
            {job.classification && badge(job.classification, clsColor)}
            {job.priority === 'urgent' && badge('URGENT', T.red)}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted }}>
            {job.clinic} · {job.province} · Submitted {new Date(job.submitted).toLocaleString()}
          </div>
          <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>Trigger: {job.trigger_reason}</div>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: `1px solid ${T.bgBorder}`, color: T.textMuted, cursor: 'pointer', fontSize: 18, borderRadius: 6, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      </div>

      {/* WGS upgrade notice */}
      {job.wgs_classification_note && (
        <div style={{ padding: '10px 20px', background: T.purple + '15', borderBottom: `1px solid ${T.purple}40`, fontSize: 12, color: T.purple }}>
          ⬆ <strong>WGS Upgrade:</strong> {job.wgs_classification_note}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${T.bgBorder}`, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '10px 16px', border: 'none', borderBottom: `2px solid ${tab === t.id ? T.cyan : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: tab === t.id ? T.cyan : T.textMuted, whiteSpace: 'nowrap', transition: 'color 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: 20, minHeight: 300 }}>
        {tab === 'pipeline' && <PipelineFlow job={job} />}
        {tab === 'qc'       && <QCPanel qc={job.qc} />}
        {tab === 'variants' && <VariantTable variants={job.variants} />}
        {tab === 'lineage'  && job.lineage ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>Lineage Classification</div>
                {[
                  ['Main Lineage',  job.lineage.main_lineage, T.cyan],
                  ['Sub-lineage',   job.lineage.sub_lineage, T.amber],
                  ['Family',        job.lineage.family, T.text],
                  ['Common Region', LINEAGES[job.lineage.main_lineage]?.common_in || '—', T.textMuted],
                ].map(([l, v, c]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${T.bgBorder}`, fontFamily: MONO, fontSize: 12 }}>
                    <span style={{ color: T.textDim }}>{l}</span>
                    <span style={{ color: c, fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
                <div style={{ marginTop: 12, padding: '10px 12px', background: T.bgElevated, borderRadius: 6, fontSize: 11, color: T.textMuted }}>
                  📝 {job.lineage.notes}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12 }}>Global Lineage Distribution</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(LINEAGES).map(([key, val]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: key === job.lineage.main_lineage ? val.color : T.textDim, flexShrink: 0, boxShadow: key === job.lineage.main_lineage ? `0 0 8px ${val.color}` : 'none' }} />
                      <span style={{ fontSize: 11, color: key === job.lineage.main_lineage ? T.text : T.textDim, fontWeight: key === job.lineage.main_lineage ? 700 : 400 }}>{key} — {val.name}</span>
                      {key === job.lineage.main_lineage && <span style={{ fontSize: 10, color: val.color, fontWeight: 700 }}>← This isolate</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : tab === 'lineage' ? <div style={{ padding: 24, textAlign: 'center', color: T.textDim }}>Lineage typing not yet available</div> : null}
        {tab === 'ml'  && <MLPanel ml={job.ml_prediction} />}
        {tab === 'log' && <TerminalLog job={job} />}
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────
export default function App() {
  const [user,       setUser]       = useState(null);
  const [loginForm,  setLoginForm]  = useState({ username: '', password: '' });
  const [loginErr,   setLoginErr]   = useState('');
  const [jobs,       setJobs]       = useState([...WGS_JOBS]);
  const [stats,      setStats]      = useState(getPipelineStats());
  const [selected,   setSelected]   = useState(null);
  const [view,       setView]       = useState('jobs');
  const [filterSt,   setFilterSt]   = useState('');
  const [tick,       setTick]       = useState(0);

  // Simulate pipeline progress for RUNNING jobs
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 3000);
    return () => clearInterval(iv);
  }, []);

  const USERS = {
    'bio.analyst':  { id: 'u1', name: 'Bioinformatics Analyst', role: 'analyst',  password: 'Bio@2026'     },
    'bio.senior':   { id: 'u2', name: 'Senior Bioinformatician',role: 'senior',   password: 'Senior@2026'  },
    'lab.manager':  { id: 'u3', name: 'Laboratory Manager',     role: 'manager',  password: 'Manager@2026' },
  };

  const doLogin = () => {
    const u = USERS[loginForm.username.trim().toLowerCase()];
    if (!u || u.password !== loginForm.password) { setLoginErr('Invalid credentials'); return; }
    setUser(u); setLoginErr('');
  };

  const filtered = jobs.filter(j => !filterSt || j.status === filterSt);

  // ── LOGIN ──
  if (!user) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.bg, position: 'relative', overflow: 'hidden' }}>
      <style>{css}</style>

      {/* Background grid */}
      <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${T.bgBorder}20 1px, transparent 1px), linear-gradient(90deg, ${T.bgBorder}20 1px, transparent 1px)`, backgroundSize: '40px 40px', opacity: 0.5 }} />

      <div style={{ width: 440, position: 'relative', zIndex: 1, animation: 'fadeUp 0.4s ease' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontFamily: MONO, fontSize: 11, color: T.textDim, marginBottom: 8, letterSpacing: 2 }}>TBGENSURV · MODULE 3</div>
          <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: T.green, letterSpacing: -1, textShadow: `0 0 30px ${T.green}40` }}>
            WGS Pipeline<span style={{ animation: 'blink 1s infinite', display: 'inline-block' }}>_</span>
          </div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 6 }}>Bioinformatics Analysis Dashboard · NMRL Zimbabwe</div>
        </div>

        <div style={{ background: T.bgCard, border: `1px solid ${T.bgBorder}`, borderRadius: 10, padding: 28 }}>
          {loginErr && <div style={{ marginBottom: 16, padding: '8px 12px', background: T.red + '20', border: `1px solid ${T.red}40`, borderRadius: 6, fontSize: 12, color: T.red, fontFamily: MONO }}>⚠ {loginErr}</div>}
          {['username', 'password'].map(f => (
            <div key={f} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: T.textDim, fontFamily: MONO, marginBottom: 5, textTransform: 'uppercase', letterSpacing: 1 }}>{f}</div>
              <input type={f === 'password' ? 'password' : 'text'} value={loginForm[f]} onChange={e => setLoginForm(p => ({ ...p, [f]: e.target.value }))} placeholder={f === 'username' ? 'bio.analyst' : '••••••••'}
                onKeyDown={e => e.key === 'Enter' && doLogin()}
                style={{ width: '100%', padding: '10px 14px', background: T.bgElevated, border: `1px solid ${T.bgBorder}`, borderRadius: 6, color: T.text, fontFamily: MONO, fontSize: 13, outline: 'none' }} />
            </div>
          ))}
          <button onClick={doLogin} style={{ width: '100%', padding: '11px', background: T.green, border: 'none', borderRadius: 6, color: T.bg, fontFamily: MONO, fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: 1 }}>
            AUTHENTICATE →
          </button>

          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 11, color: T.textDim, cursor: 'pointer', fontFamily: MONO }}>demo_credentials.txt ▾</summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {Object.entries(USERS).map(([un, u]) => (
                <div key={un} onClick={() => setLoginForm({ username: un, password: u.password })}
                  style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: T.bgElevated, borderRadius: 5, cursor: 'pointer', fontFamily: MONO, fontSize: 11 }}>
                  <span style={{ color: T.green }}>{un}</span>
                  <span style={{ color: T.textDim }}>{u.role}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <style>{css}</style>

      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 24px', background: T.bgCard, borderBottom: `1px solid ${T.bgBorder}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: T.green }}>TBGenSurv<span style={{ color: T.textDim }}>/</span>module3</span>
          <span style={{ fontFamily: MONO, fontSize: 10, color: T.textDim, background: T.bgElevated, padding: '3px 10px', borderRadius: 99, border: `1px solid ${T.bgBorder}` }}>WGS_PIPELINE · H37Rv</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {jobs.filter(j => j.status === 'RUNNING').length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', background: T.cyan + '15', borderRadius: 99, border: `1px solid ${T.cyan}40`, fontSize: 11, color: T.cyan, fontFamily: MONO }}>
              <Spinner size={10} color={T.cyan} />
              {jobs.filter(j => j.status === 'RUNNING').length} running
            </div>
          )}
          <span style={{ fontFamily: MONO, fontSize: 12, color: T.textMuted }}>{user.name}</span>
          <button onClick={() => setUser(null)} style={{ padding: '5px 12px', background: 'none', border: `1px solid ${T.bgBorder}`, borderRadius: 6, color: T.textMuted, cursor: 'pointer', fontSize: 12 }}>logout</button>
        </div>
      </div>

      <div style={{ flex: 1, padding: 24, maxWidth: 1600, margin: '0 auto', width: '100%' }}>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 10, marginBottom: 24 }}>
          {[
            { label: 'Total Jobs',    value: stats.total_jobs,    color: T.cyan,   icon: '🧬' },
            { label: 'Queued',        value: stats.queued,        color: T.textMuted,icon: '⏳' },
            { label: 'Running',       value: stats.running,       color: T.cyan,   icon: '▶', pulse: true },
            { label: 'Complete',      value: stats.complete,      color: T.green,  icon: '✓' },
            { label: 'Failed',        value: stats.failed,        color: T.red,    icon: '✗' },
            { label: 'MDR Confirmed', value: stats.mdr_confirmed, color: T.red,    icon: '⚠' },
            { label: 'Avg Depth',     value: `${stats.avg_depth}×`,color: T.amber, icon: '📊' },
            { label: 'Avg Coverage',  value: `${stats.avg_coverage}%`,color: T.green,icon: '🎯' },
          ].map(s => (
            <div key={s.label} style={{ background: T.bgCard, border: `1px solid ${T.bgBorder}`, borderRadius: 8, padding: '14px 12px', borderTop: `2px solid ${s.color}`, cursor: 'default' }}>
              <div style={{ fontSize: 18, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 5, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selected ? '380px 1fr' : '1fr', gap: 20 }}>

          {/* Job list */}
          <div>
            {/* Filter chips */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {[['', 'All'], ['QUEUED', 'Queued'], ['RUNNING', 'Running'], ['COMPLETE', 'Complete'], ['FAILED', 'Failed']].map(([v, l]) => (
                <button key={v} onClick={() => setFilterSt(v)} style={{ padding: '5px 12px', borderRadius: 99, border: `1px solid ${filterSt === v ? T.cyan : T.bgBorder}`, background: filterSt === v ? T.cyan + '20' : T.bgCard, cursor: 'pointer', fontSize: 11, fontFamily: MONO, color: filterSt === v ? T.cyan : T.textMuted, transition: 'all 0.15s' }}>{l}</button>
              ))}
              <div style={{ flex: 1 }} />
              <button onClick={() => { const j = submitJob(`TBS-ZWE-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${String(jobs.length+1).padStart(4,'0')}`, `smp-new-${Date.now()}`, 'MDR-TB — Test submission', user.id, 'Test Clinic', 'Harare'); setJobs([...WGS_JOBS]); setStats(getPipelineStats()); }}
                style={{ padding: '5px 14px', borderRadius: 6, border: `1px solid ${T.green}50`, background: T.green + '15', cursor: 'pointer', fontSize: 11, fontFamily: MONO, color: T.green }}>
                + Submit Job
              </button>
            </div>

            {/* Job cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map(job => {
                const st = JOB_STATUS[job.status];
                const clsColor = TB_CLASS_COLORS[job.classification] || T.textDim;
                const isSelected = selected?.id === job.id;
                return (
                  <div key={job.id} onClick={() => setSelected(job)} style={{ background: T.bgCard, border: `1px solid ${isSelected ? T.cyan : T.bgBorder}`, borderLeft: `3px solid ${st.color}`, borderRadius: 8, padding: '14px 16px', cursor: 'pointer', transition: 'all 0.15s', boxShadow: isSelected ? `0 0 16px ${T.cyan}20` : 'none' }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = T.textDim; }}
                    onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = T.bgBorder; }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <span style={{ fontFamily: MONO, fontSize: 12, color: T.cyan }}>{job.sample_code}</span>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        {job.status === 'RUNNING' && <Spinner size={10} color={T.cyan} />}
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, color: st.color, background: st.bg + '40', border: `1px solid ${st.color}30`, fontWeight: 600 }}>{st.label}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{job.clinic} · {job.province}</div>
                    <div style={{ fontSize: 10, color: T.textDim, marginBottom: 8 }}>{job.trigger_reason}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {job.classification && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, color: clsColor, background: clsColor + '20', border: `1px solid ${clsColor}30`, fontWeight: 700 }}>{job.classification}</span>}
                        {job.priority === 'urgent' && <span style={{ fontSize: 10, color: T.red }}>URGENT</span>}
                      </div>
                      <span style={{ fontSize: 10, color: T.textDim, fontFamily: MONO }}>{new Date(job.submitted).toLocaleDateString()}</span>
                    </div>

                    {/* Progress bar for running jobs */}
                    {job.status === 'RUNNING' && (() => {
                      const done = job.stages.filter(s => s.status === 'complete').length;
                      const total = job.stages.length;
                      const pct = Math.round((done / total) * 100);
                      return (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: T.textDim, marginBottom: 3, fontFamily: MONO }}>
                            <span>Stage {done}/{total}</span><span>{pct}%</span>
                          </div>
                          <div style={{ height: 3, background: T.bgElevated, borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${T.cyan}, ${T.green})`, borderRadius: 2 }} />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          {selected && <JobDetail job={selected} onClose={() => setSelected(null)} />}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '8px 24px', background: T.bgCard, borderTop: `1px solid ${T.bgBorder}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.textDim, fontFamily: MONO }}>
        <span>TBGenSurv/module3 · WGS Pipeline Dashboard · Ref: H37Rv AL123456.3</span>
        <span>BWA-MEM · GATK · snippy · WHO 2021 Catalogue · TB-Profiler · k-mer ML</span>
      </div>
    </div>
  );
}
